import type { Branding } from '@helios/ui';

type Props = {
  branding: Branding;
};

// Koncept Skin – warm beige + cocoa aesthetic, inspired by their website banners.
// Intended to be passed as BrandedLoginShell's leftSlot (full left panel override).
export function KonceptSkinLoginLeft({ branding }: Props) {
  // Website-inspired default: cocoa brown. Can still be overridden via tenant branding/theme.
  const primaryColor = branding.theme?.primaryColor ?? '#6B3F1D';

  // Warm skin-tone neutrals
  const blush = '#F3D7D2';
  const sand = '#F6EFE6';
  const cream = '#FFF9F3';
  const ink = '#1f2937';
  const muted = '#4b5563';

  const bullets = [
    'Dermatologist-led care supported by clear, personalized treatment plans',
    'Aesthetics & skin services tracked with structured notes and follow-up reminders',
    'Secure access for doctors and staff — patient data stays protected',
  ];

  const brandName = branding.orgName || 'Koncept Skin';

  // Optional client hero image.
  // If you keep the file next to this component (apps/web-admin/src/clients/koncept-skin/hero.png),
  // use a bundled asset URL so it works in dev/prod without touching /public.
  const heroImage = '/clients/koncept-skin/hero.png';

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        // Refresh-smiles style: soft wash layers; hero photo is rendered as a masked layer below
        backgroundImage:
          `radial-gradient(1100px 560px at 12% 8%, ${cream}F0 0%, ${cream}9A 34%, ${cream}00 72%),` +
          `radial-gradient(900px 520px at 92% 12%, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.58) 38%, rgba(255,255,255,0.00) 74%),` +
          `linear-gradient(160deg, ${cream}92 0%, rgba(255,255,255,0.68) 46%, rgba(248,250,252,0.78) 100%)`,
        backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
        backgroundSize: '100% 100%, 100% 100%, 100% 100%',
        backgroundPosition: 'center, center, center',
      }}
    >
      {/* Soft spa-like shapes */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      >
        {/* Hero photo layer with feathered edges so it blends into the wash */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            backgroundImage: `url('${heroImage}')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '118% auto',
            backgroundPosition: '66% 16%',
            opacity: 0.92,
            filter: 'saturate(1.03) contrast(1.04)',
            // Feather the edges (works across modern browsers)
            WebkitMaskImage:
              'radial-gradient(130% 105% at 66% 42%, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 46%, rgba(0,0,0,0.55) 64%, rgba(0,0,0,0) 90%), linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 16%, rgba(0,0,0,1) 86%, rgba(0,0,0,0) 100%), linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 14%, rgba(0,0,0,1) 84%, rgba(0,0,0,0) 100%)',
            maskImage:
              'radial-gradient(130% 105% at 66% 42%, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 46%, rgba(0,0,0,0.55) 64%, rgba(0,0,0,0) 90%), linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 16%, rgba(0,0,0,1) 86%, rgba(0,0,0,0) 100%), linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 14%, rgba(0,0,0,1) 84%, rgba(0,0,0,0) 100%)',
          }}
        />

        {/* Left-side readability wash (prevents copy from sitting on top of the portrait) */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage:
              'linear-gradient(90deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.86) 42%, rgba(255,255,255,0.00) 78%)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
            backgroundPosition: 'left center',
          }}
        />

        {/* Extra edge wash to hide any hard photo boundaries on large screens */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage:
              `radial-gradient(1000px 600px at 70% 42%, rgba(255,255,255,0.00) 0%, rgba(255,255,255,0.38) 44%, rgba(255,255,255,0.72) 66%, rgba(255,255,255,0.94) 88%),` +
              `linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.00) 18%, rgba(255,255,255,0.00) 82%, rgba(255,255,255,0.92) 100%),` +
              `linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.00) 34%, rgba(255,255,255,0.80) 100%)`,
            backgroundRepeat: 'no-repeat, no-repeat',
            backgroundSize: '100% 100%, 100% 100%',
            backgroundPosition: 'center, center',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.2,
            filter: 'blur(70px)',
            backgroundColor: blush,
            width: '26rem',
            height: '26rem',
            top: '-8rem',
            left: '-8rem',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.14,
            filter: 'blur(70px)',
            backgroundColor: primaryColor,
            width: '20rem',
            height: '20rem',
            top: '18%',
            left: '18%',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.18,
            filter: 'blur(80px)',
            backgroundColor: sand,
            width: '600px',
            height: '600px',
            bottom: '-14rem',
            left: '16%',
          }}
        />
      </div>

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        {/* Top nav (website-like) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1.35rem 4rem 1.0rem 4rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {branding.orgLogoHorizontalUrl ? (
              <img
                src={branding.orgLogoHorizontalUrl}
                alt={brandName}
                style={{ height: '7.5rem', width: 'auto', objectFit: 'contain' }}
              />
            ) : branding.orgLogoUrl ? (
              <>
                <img
                  src={branding.orgLogoUrl}
                  alt={brandName}
                  style={{ height: '5.0rem', width: 'auto', objectFit: 'contain' }}
                />
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: ink }}>
                  {brandName}
                </span>
              </>
            ) : (
              <span style={{ fontSize: '1.25rem', fontWeight: 700, color: ink }}>{brandName}</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.75rem' }} />
        </div>

        {/* Lower copy section */}
        <div
          style={{
            padding: '3.25rem 4rem 2.25rem 4rem',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={{ maxWidth: '44rem' }}>
            <div
              style={{
                fontSize: '0.9rem',
                letterSpacing: '0.08em',
                color: primaryColor,
                textTransform: 'uppercase',
                maxWidth: '44rem',
              }}
            >
              Confidence begins with clarity.
            </div>
            <div
              style={{
                marginTop: '0.65rem',
                fontSize: '1.05rem',
                color: ink,
                opacity: 0.8,
                maxWidth: '44rem',
              }}
            >
              Track consultations, treatments, and follow-ups with clarity — so every visit feels
              personal, consistent, and well-documented.
            </div>

            <ul
              style={{
                marginTop: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.9rem',
                maxWidth: '44rem',
              }}
            >
              {bullets.map((text) => (
                <li
                  key={text}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.85rem',
                    color: muted,
                  }}
                >
                  <span
                    style={{
                      marginTop: '0.25rem',
                      flexShrink: 0,
                      width: '1.25rem',
                      height: '1.25rem',
                      borderRadius: '9999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${primaryColor}18`,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: '0.5rem',
                        height: '0.5rem',
                        borderRadius: '9999px',
                        backgroundColor: primaryColor,
                        display: 'block',
                      }}
                    />
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            style={{
              marginTop: 'auto',
              paddingTop: '2.5rem',
              fontSize: '0.875rem',
              color: '#94a3b8',
            }}
          >
            Powered by {branding.poweredBy ?? 'Helios Core'}
          </div>
        </div>
      </div>
    </div>
  );
}
